import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ResetPasswordDialog } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useSuccessToast } from "@/hooks";
import { useUpdateUser } from "@/hooks/mutations";
import { useSetCompanyUserInCompanyUsersForSettings } from "@/hooks/sets";
import { storage } from "@/lib/storage";

const updateUserSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  email: z.string().min(1, "Required"),
});

export const UpdateUserForm = () => {
  const { mutate: updateUser } = useUpdateUser();
  const setCompanyUserInCompanyUsersForSettings =
    useSetCompanyUserInCompanyUsersForSettings();
  const successToast = useSuccessToast();

  // TODO: This is a common pattern so we should create a reusable piece of logic.
  const user = storage.getUser();
  if (!user) {
    throw new Error("User not found in local storage");
  }

  const form = useForm<z.infer<typeof updateUserSchema>>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    },
    resolver: zodResolver(updateUserSchema),
  });

  const onSubmit = (values: z.infer<typeof updateUserSchema>) => {
    updateUser(
      {
        updateUserRequest: {
          firstName: values.firstName,
          lastName: values.lastName,
        },
        userId: user.id,
      },
      {
        onSuccess: () => {
          setCompanyUserInCompanyUsersForSettings({
            companyUserToSet: {
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              isInvited: false,
              invitationStatus: null,
              inviterId: null,
            },
          });
          successToast({
            description: "User updated successfully!",
          });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        className="flex max-w-xl flex-col gap-6"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="First Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Last Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            disabled
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="Email" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center justify-between">
          <ResetPasswordDialog />
          <Button className="rounded-full" type="submit">
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};
